import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import { BarChart } from '@mui/x-charts/BarChart';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ExamResults from './ExamResults';
import { mislecAPI } from '../../../util/axios';
import StatisticCard from '../../admin/StatisticCard';

function Analytics({ course, user, updateUser }) {
    const { t } = useTranslation();
    const [analyticData, setAnalyticData] = React.useState(null);
    const [statData, setStatData] = React.useState([]);
    const [FoMSectionsData, setFoMSectionsData] = React.useState([]);
    const [graphKeys, setGraphKeys] = React.useState([]);
    const [sectionViewsData, setSectionViewsData] = React.useState([]);
    const [sectionCountData, setSectionCountData] = React.useState([]);
    const [sectionTimeData, setSectionTimeData] = React.useState([]);
    const [examsData, setExamsData] = React.useState([]);
    const [examAttempts, setExamAttempts] = React.useState(null);
    const [error, setError] = React.useState(null);

    const graphColors = ['#73BAFB', '#C684FF', '#5BE49B', '#FFD666'];

    const examsIcons = [
        {
            // total time spent
            icon: 'tabler:file-time',
            label: t('course.tabs.analytics.data.exams.time.total.title'),
            width: 48,
            tooltip: t('course.tabs.analytics.data.exams.time.total.tooltip')
        },
        {
            // avg time spent exam
            icon: 'tdesign:task-time',
            label: t('course.tabs.analytics.data.exams.time.average.title'),
            width: 48,
            tooltip: t('course.tabs.analytics.data.exams.time.average.tooltip')
        },
        {
            // average percent
            icon: 'ic:baseline-grade',
            label: t('course.tabs.analytics.data.exams.percent.average.title'),
            width: 48,
            tooltip: t('course.tabs.analytics.data.exams.percent.average.tooltip')
        },
        {
            // passed / all people
            icon: 'gg:check-o',
            label: t('course.tabs.analytics.data.exams.ratio.passVfail.title'),
            width: 48,
            tooltip: t('course.tabs.analytics.data.exams.ratio.passVfail.tooltip')
        }
    ];

    function formatDuration(ms) {
        const totalMinutes = Math.floor(ms / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const seconds = Math.floor((ms % 60000) / 1000);


        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m`;
        } else {
            return `${seconds}s`;
        }
    }

    const getAnalyticsData = async () => {
        try {
            const response = await mislecAPI.get(`/api/course/analytics/${course._id}`);

            const data = response.data;

            getFoMSectionsGraphData(data);
            getSectionsGraphData(data);
            createExamsData(data);

            setAnalyticData(data);
            setError(null);
        } catch (error) {
            if (error.response?.data?.detail === 'api.auth.verify.401') {
                updateUser(null);
                return;
            }

            setError(error.response?.data?.detail || 'api.general.500');
        }
    }

    const getFoMSectionsGraphData = (data) => {
        const FoMData = data.find_out_more_analytics;

        const tempSectionsData = [];

        if (FoMData) {
            const keys = Object.keys(FoMData);
            for (const key of keys) {
                tempSectionsData.push(FoMData[key]);
            }
        }

        setFoMSectionsData(tempSectionsData);
    }

    const getSectionsGraphData = (data) => {
        const tempSectionsData = data.sections;

        const ids = tempSectionsData.map((item) => { return (item.title ? item.title : item.id) });
        const tempViewData = tempSectionsData.map((item) => { return item.views });
        const tempCountData = tempSectionsData.map((item) => { return item.count });
        const tempTimeData = tempSectionsData.map((item) => { return Math.round((item.time / 1000 / 60) * 100) / 100 });

        setGraphKeys(ids);
        setSectionViewsData(tempViewData);
        setSectionCountData(tempCountData);
        setSectionTimeData(tempTimeData);
    }

    const createExamsData = (data) => {
        const tempExamsData = data.exams;

        const tempExamsAnalyticData = [];

        if (tempExamsData) {
            tempExamsData.forEach((exam) => {
                const tempCurrentExamData = [];
                examsIcons.forEach((examIcon, index) => {
                    const iconObject = {
                        id: exam.id,
                        title: exam.title,
                        value: index === 0 ? formatDuration(exam.total_time)
                            : index === 1 ? formatDuration(exam.avg_time)
                                : index === 2 ? `${Math.round(exam.avg_points * 100)}%`
                                    : `${(exam.no_passed)} / ${exam.no_passed + exam.no_failed}`,
                        ...examIcon
                    };

                    tempCurrentExamData.push(iconObject);
                })

                tempExamsAnalyticData.push(tempCurrentExamData);
            })
        }

        setExamsData(tempExamsAnalyticData);
    }

    const getExamAttempts = async () => {
        const fetchExamAttempts = async (subsection) => {
            try {
                const res = await mislecAPI.get(`api/course/exams/${subsection.id}`);
                return res.data.exams;
            } catch (error) {
                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);

                return null;
            }
        }

        let attempts = [];
        for (const section of course.sections) {
            if (section.subsections[0] && section.subsections[0].quiz?.type === 'exam' && section.subsections[0].quiz)
                attempts.push({ id: section.subsections[0].id, visibility: false, attempts: await fetchExamAttempts(section.subsections[0]) });
        }

        // Sort exam attempts to match examsData order
        const sortedExams = [];
        examsData.forEach((exam) => {
            const foundAttempt = attempts.find(attempt => attempt.id === exam[0].id);
            if (foundAttempt) {
                sortedExams.push(foundAttempt);
            }
        });
        setExamAttempts(sortedExams);
    }

    React.useEffect(() => {
        getAnalyticsData();
    }, []);

    React.useEffect(() => {
        getExamAttempts();
    }, [examsData]);

    React.useEffect(() => {
        const tempStatData = [
            {
                // avg time spent
                icon: 'mingcute:time-line',
                label: t('course.tabs.analytics.data.time.course.title'),
                value: analyticData?.average_time_spent ? formatDuration(analyticData?.average_time_spent) : formatDuration(0),
                width: 48,
                tooltip: t('course.tabs.analytics.data.time.course.tooltip')
            },
            {
                // avg time spent sections
                icon: 'fluent-mdl2:screen-time',
                label: t('course.tabs.analytics.data.time.sections.title'),
                value: analyticData?.average_time_spent_on_section ? formatDuration(analyticData?.average_time_spent_on_section) : formatDuration(0),
                width: 48,
                tooltip: t('course.tabs.analytics.data.time.sections.tooltip')
            },
            {
                // avg section views
                icon: 'hugeicons:view',
                label: t('course.tabs.analytics.data.views.sections.title'),
                value: analyticData?.average_views_on_section ? Math.round(analyticData?.average_views_on_section * 100) / 100 : 0,
                width: 48,
                tooltip: t('course.tabs.analytics.data.views.sections.tooltip')
            },
            {
                // avg messages sent
                icon: 'tabler:message',
                label: t('course.tabs.analytics.data.messages.sent.title'),
                value: analyticData?.average_no_of_messages ? Math.round(analyticData?.average_no_of_messages * 100) / 100 : 0,
                width: 48,
                tooltip: t('course.tabs.analytics.data.messages.sent.tooltip')
            },
            {
                // avg no find out more messages
                icon: 'tabler:message-star',
                label: t('course.tabs.analytics.data.messages.findOutMore.title'),
                value: analyticData?.no_of_find_out_more_messages ? Math.round(analyticData?.no_of_find_out_more_messages * 100) / 100 : 0,
                width: 48,
                tooltip: t('course.tabs.analytics.data.messages.findOutMore.tooltip')
            },
            {
                // ratio reg msg vs find out more msg
                icon: 'icon-park-outline:equal-ratio',
                label: t('course.tabs.analytics.data.messages.ratio.title'),
                value: analyticData?.percent_of_find_out_more_messages ? Math.round(analyticData?.percent_of_find_out_more_messages * 100) + ' %' : 0,
                width: 48,
                tooltip: t('course.tabs.analytics.data.messages.ratio.tooltip')
            }
        ];

        setStatData(tempStatData);
    }, [analyticData]);

    const handleVisibility = (index) => {
        let attempts = [...examAttempts];
        attempts[index].visibility = !attempts[index].visibility;
        setExamAttempts(attempts);
    }

    return (
        <>
            <Grid container sx={{ ml: 1, pr: 2 }}>
                <Grid container spacing={2} sx={{ mt: 1, mb: 2, pt: 0 }}>
                    <Grid item xs={12} sx={{ pt: '0px !important' }}>
                        <Typography variant='h5'>{t('course.tabs.analytics.information.title')} {course.title}</Typography>
                        <Typography variant='body2' sx={{ mt: 1 }}>
                            {t('course.tabs.analytics.information.description')}
                        </Typography>
                    </Grid>
                    {error && (
                        <Grid item xs={12}>
                            <Typography variant='body2' color='error'>
                                {t(error)}
                            </Typography>
                        </Grid>
                    )}

                    <Grid container spacing={2} sx={{ mb: { xs: 3, md: 2 }, mt: { xs: 3, md: 2 }, pl: 2 }}>
                        {statData.map((card, index) => (
                            <StatisticCard
                                key={index}
                                icon={card.icon}
                                label={card.label}
                                value={card.value}
                                width={card.width}
                                tooltip={card.tooltip}
                                mdSize={4}
                            />
                        ))}
                    </Grid>

                    <Grid item xs={12} sx={{ mb: 2, mt: 0, pt: 2, pl: 2 }}>
                        <Divider />
                    </Grid>

                    {/* GRAPHS */}
                    <Grid container spacing={2} sx={{ mb: { xs: 3, md: 0 }, mt: { xs: 3, md: 0 }, pl: 2 }}>
                        {/* FIND OUT MORE PER SECTION */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', pt: '0px !important' }}>
                            <Typography variant='h5'>{t('course.tabs.analytics.graphs.FoM.title')}</Typography>
                            <Typography variant='body2' sx={{ mt: 1 }}>
                                {t('course.tabs.analytics.graphs.FoM.description')}
                            </Typography>
                            {analyticData && analyticData.find_out_more_analytics && <BarChart
                                xAxis={[{
                                    scaleType: 'band',
                                    data: Object.keys(analyticData.find_out_more_analytics) || [{ data: [] }]
                                }]}
                                series={[{ data: FoMSectionsData, color: graphColors[0] }]}
                                height={300}
                            />}
                        </Grid>

                        {/* SECTION VIEWS ANALYTICS */}
                        <Grid item xs={12} md={6} sx={{ pt: '0px !important' }}>
                            <Typography variant='h5'>{t('course.tabs.analytics.graphs.views.title')}</Typography>
                            <Typography variant='body2' sx={{ mt: 1 }}>
                                {t('course.tabs.analytics.graphs.views.description')}
                            </Typography>
                            {analyticData && analyticData.sections && <BarChart
                                xAxis={[{
                                    scaleType: 'band',
                                    data: graphKeys || [{ data: [] }]
                                }]}
                                series={[{ data: sectionViewsData, color: graphColors[1] }]}
                                height={300}
                            />}
                        </Grid>

                        {/* SECTION COUNT ANALYTICS */}
                        <Grid item xs={12} md={6} sx={{ pt: '0px !important' }}>
                            <Typography variant='h5'>{t('course.tabs.analytics.graphs.count.title')}</Typography>
                            <Typography variant='body2' sx={{ mt: 1 }}>
                                {t('course.tabs.analytics.graphs.count.description')}
                            </Typography>
                            {analyticData && analyticData.sections && <BarChart
                                xAxis={[{
                                    scaleType: 'band',
                                    data: graphKeys || [{ data: [] }]
                                }]}
                                series={[{ data: sectionCountData, color: graphColors[2] }]}
                                height={300}
                            />}
                        </Grid>

                        {/* SECTION TIME ANALYTICS */}
                        <Grid item xs={12} md={6} sx={{ pt: '0px !important' }}>
                            <Typography variant='h5'>{t('course.tabs.analytics.graphs.time.title')}</Typography>
                            <Typography variant='body2' sx={{ mt: 1 }}>
                                {t('course.tabs.analytics.graphs.time.description')}
                            </Typography>
                            {analyticData && analyticData.sections && <BarChart
                                // margin={{left: 100}}
                                xAxis={[{
                                    scaleType: 'band',
                                    data: graphKeys || [{ data: [] }]
                                }]}
                                series={[{ data: sectionTimeData, color: graphColors[3] }]}
                                height={300}
                            />}
                        </Grid>
                    </Grid>

                    {/* EXAMS */}
                    {(examsData.length > 0) && examsData.map((exam, index) => (
                        (
                            <Grid container sx={{ pl: 2 }}>
                                <Grid item xs={12} sx={{ mb: 2, mt: 0, pt: 2 }}>
                                    <Divider />
                                </Grid>

                                <Typography variant='h5'>
                                    {`${t('course.tabs.analytics.data.exams.title')} ${(exam[0].title ? exam[0].title : exam[0].id)}`}
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: { xs: 3, md: 1 }, mt: { xs: 3, md: 0 } }}>
                                    {exam.map((examCard, index) => (
                                        <StatisticCard
                                            key={examCard.id + '-' + index}
                                            icon={examCard.icon}
                                            label={examCard.label}
                                            value={examCard.value}
                                            width={examCard.width}
                                            tooltip={examCard.tooltip}
                                            mdSize={6}
                                        />
                                    ))}
                                </Grid>

                                {examAttempts && examAttempts.length > 0 &&
                                    <>
                                        <Grid container>
                                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', pr: 1 }}>
                                                <IconButton
                                                    onClick={() => handleVisibility(index)}
                                                    disabled={!examAttempts[index] || examAttempts[index]?.attempts.length === 0}
                                                    sx={{
                                                        p: '4px',
                                                        borderRadius: '8px'
                                                    }}
                                                >
                                                    <Typography variant='body2' sx={{ ml: 0.5, fontSize: '0.8rem', fontWeight: '700' }}>
                                                        {t(examAttempts[index]?.visibility ? 'course.tabs.analytics.data.exams.buttons.hideAttempts' : 'course.tabs.analytics.data.exams.buttons.showAttempts')}
                                                    </Typography>
                                                    {examAttempts[index]?.visibility ? <ExpandLessIcon sx={{ width: 18 }} /> : <ExpandMoreIcon sx={{ width: 18 }} />}
                                                </IconButton>
                                            </Grid>
                                        </Grid>

                                        <Grid container>
                                            <Grid item xs={12}>
                                                <Collapse in={examAttempts[index]?.visibility}>
                                                    <Grid container>
                                                        <Swiper
                                                            spaceBetween={10}
                                                            slidesPerView={1}
                                                            modules={[Pagination]}
                                                            pagination={{ clickable: true }}
                                                            observer={true}
                                                            observeParents={true}
                                                            autoHeight
                                                            className={`mISLec_swiper_result ${examAttempts[index]?.attempts.length > 1 ? 'mISLec_swiper_results' : ''}`}
                                                        >
                                                            {examAttempts[index]?.attempts && examAttempts[index]?.attempts.map((result, resIndex) => (
                                                                <SwiperSlide key={resIndex} style={{ overflow: 'visible' }}>
                                                                    <Grid item xs={12}>
                                                                        <ExamResults result={result} user={user} />
                                                                    </Grid>
                                                                </SwiperSlide>
                                                            ))}
                                                        </Swiper>
                                                    </Grid>
                                                </Collapse>
                                            </Grid>
                                        </Grid>
                                    </>
                                }
                            </Grid>
                        )
                    ))}
                </Grid>
            </Grid>
        </>
    );
}

export default Analytics;
